/**
 * Super Admin device / app version diagnostics.
 *
 * Captures a small, non-identifying description of the device the app is
 * running on (platform, model, OS version, app version + build) so Super Admin
 * can tell what a user is actually running when they report a problem.
 *
 * Rules:
 *  - Completely non-blocking and best-effort. Any failure is swallowed.
 *  - Never collects IMEI, serial numbers, contacts, location or any other
 *    sensitive data. The only identifier used is Capacitor's per-vendor
 *    install id (already a safe, non-personal identifier).
 *  - Pure helpers below are exported for tests; nothing here mutates app state.
 */
import { supabase } from "@/integrations/supabase/client";
import { APP_VERSION } from "@/lib/appVersion";
import { getReleaseInfo, getRuntimeContext } from "@/lib/releaseInfo";
import { logger } from "@/lib/logger";

export type DevicePlatform = "ios" | "android" | "web";

export interface DeviceDiagnosticsRecord {
  device_id: string;
  platform: DevicePlatform;
  device_model: string | null;
  os_name: string | null;
  os_version: string | null;
  app_version: string | null;
  app_build: string | null;
}

/** Raw shapes we accept from the Capacitor Device plugin (all fields optional). */
export interface RawDeviceInfo {
  platform?: string;
  model?: string;
  operatingSystem?: string;
  osVersion?: string;
  manufacturer?: string;
}

export interface RawAppInfo {
  version?: string;
  build?: string;
}

function normalisePlatform(value: unknown): DevicePlatform {
  const v = typeof value === "string" ? value.toLowerCase() : "";
  if (v === "ios") return "ios";
  if (v === "android") return "android";
  return "web";
}

function clean(value: unknown, max = 60): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

/**
 * Build the row we persist from whatever the platform managed to report.
 * Missing values are stored as null — they never abort the capture.
 */
export function buildDeviceRecord(
  device: RawDeviceInfo | null | undefined,
  app: RawAppInfo | null | undefined,
  deviceId: string | null | undefined,
  fallback?: { osVersion?: string; deviceModel?: string; appVersion?: string; appBuild?: string },
): DeviceDiagnosticsRecord {
  const platform = normalisePlatform(device?.platform);
  const osName =
    clean(device?.operatingSystem) ??
    (platform === "ios" ? "iOS" : platform === "android" ? "Android" : null);

  const manufacturer = clean(device?.manufacturer, 30);
  const model = clean(device?.model, 40) ?? clean(fallback?.deviceModel, 40);
  const deviceModel =
    platform === "android" && manufacturer && model && !model.toLowerCase().startsWith(manufacturer.toLowerCase())
      ? `${manufacturer} ${model}`.slice(0, 60)
      : model;

  return {
    device_id: clean(deviceId, 80) ?? "unknown",
    platform,
    device_model: deviceModel,
    os_name: osName ? osName.replace(/^ios$/i, "iOS") : null,
    os_version: clean(device?.osVersion, 30) ?? clean(fallback?.osVersion, 30),
    app_version: clean(app?.version, 20) ?? clean(fallback?.appVersion, 20) ?? APP_VERSION,
    app_build: clean(app?.build, 20) ?? clean(fallback?.appBuild, 20),
  };
}

/** Collect device diagnostics, preferring the Capacitor Device API. */
export async function collectDeviceDiagnostics(): Promise<DeviceDiagnosticsRecord> {
  const release = getReleaseInfo();
  const runtime = getRuntimeContext();
  const fallback = {
    osVersion: runtime.os_version,
    deviceModel: runtime.device_model,
    appVersion: release.appVersion,
    appBuild: release.buildNumber,
  };

  try {
    const { Device } = await import("@capacitor/device");
    const info = (await Device.getInfo()) as RawDeviceInfo;
    let deviceId: string | null = null;
    try {
      deviceId = ((await Device.getId()) as { identifier?: string })?.identifier ?? null;
    } catch {
      /* identifier is optional */
    }

    let appInfo: RawAppInfo | null = null;
    if (normalisePlatform(info?.platform) !== "web") {
      try {
        const { App } = await import("@capacitor/app");
        appInfo = (await App.getInfo()) as RawAppInfo;
      } catch {
        /* app info unavailable — fall back to the bundle constants */
      }
    }

    return buildDeviceRecord(info, appInfo, deviceId, fallback);
  } catch (error) {
    logger.warn("[DeviceDiagnostics] collection fell back to browser info", error);
    return buildDeviceRecord(
      { platform: release.isNative ? release.platform.replace("native-", "") : "web" },
      null,
      null,
      fallback,
    );
  }
}

/**
 * Upsert the authenticated user's latest device information.
 * Never throws — diagnostics must never affect app behaviour.
 */
export async function recordDeviceInfo(userId: string, companyId: string | null): Promise<boolean> {
  try {
    if (!userId) return false;
    const record = await collectDeviceDiagnostics();
    const { error } = await supabase
      .from("user_device_info")
      .upsert(
        {
          user_id: userId,
          company_id: companyId,
          ...record,
          last_seen_at: new Date().toISOString(),
        },
        { onConflict: "user_id,device_id" },
      );
    if (error) {
      logger.warn("[DeviceDiagnostics] upsert failed", error.message);
      return false;
    }
    return true;
  } catch (error) {
    logger.warn("[DeviceDiagnostics] capture failed", error);
    return false;
  }
}
